/**
 * Client-side implementation of renderToString for frontend code.
 *
 * This provides a lightweight (~1KB) alternative to react-dom/server (~500KB)
 * for rendering React elements to HTML strings in the browser.
 *
 * Used by Highcharts tooltip/label formatters that need to render React
 * components to HTML strings.
 *
 * Usage:
 *   import { renderToString } from 'shared/frontend/stubs/react-dom-server-client';
 *
 * NOTE: For new code, consider using template literals instead of renderToString
 * when possible, as this approach has overhead from DOM node creation.
 */
import type { ReactElement } from 'react';
import { createRoot } from 'react-dom/client';
import { flushSync } from 'react-dom';

/**
 * Renders a React element to an HTML string.
 *
 * This is a client-side implementation that uses createRoot + flushSync
 * to render to an offscreen DOM node, then extracts innerHTML.
 *
 * Note: This only works in browser environments. During SSR, returns empty string.
 */
export function renderToString(element: ReactElement): string {
  if (typeof document === 'undefined') {
    return ''; // SSR guard - not available on server
  }

  const container = document.createElement('div');
  const root = createRoot(container);

  try {
    flushSync(() => {
      root.render(element);
    });

    const payload = container.innerHTML;
    root.unmount();
    container.remove();
    return payload;
  } finally {
    root.unmount();
  }
}

/**
 * Renders a React element to a static HTML string.
 * For client-side usage, this is identical to renderToString.
 */
export function renderToStaticMarkup(element: ReactElement): string {
  return renderToString(element);
}

// Default export for convenience
export default {
  renderToString,
  renderToStaticMarkup
};
